import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import UserDetailModal from './UserDetailModal';
import {
  Users,
  Search,
  Loader2,
  User,
  Stethoscope,
  Building2,
  Shield,
  UserCog,
  Eye,
  Ban,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  Filter,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const EnhancedUsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1
  });
  
  // Filters
  const [filters, setFilters] = useState({
    role: 'all',
    status: 'all',
    search: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });
  
  const [searchInput, setSearchInput] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(f => ({ ...f, search: searchInput }));
      setPagination(p => ({ ...p, page: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadUsers();
  }, [filters, pagination.page]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sort_by: filters.sortBy,
        sort_order: filters.sortOrder,
      };
      
      if (filters.role !== 'all') params.role = filters.role;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.search) params.search = filters.search;
      
      const response = await adminAPI.getUsers(params);
      setUsers(response.data.users);
      setPagination(p => ({
        ...p,
        total: response.data.total,
        pages: response.data.pages
      }));
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickSuspend = async (userId, currentStatus) => {
    try {
      await adminAPI.suspendUser(userId, !currentStatus);
      toast.success(currentStatus ? 'Utilisateur réactivé' : 'Utilisateur suspendu');
      loadUsers();
    } catch (error) {
      toast.error('Erreur lors de l\'opération');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await adminAPI.exportUsers(filters.role !== 'all' ? filters.role : null);
      
      // Create and download CSV
      const data = response.data.data;
      if (data.length === 0) {
        toast.error('Aucune donnée à exporter');
        return;
      }
      
      const headers = ['Nom', 'Email', 'Téléphone', 'Rôle', 'Localisation', 'Date inscription', 'Statut'];
      const csvContent = [
        headers.join(','),
        ...data.map(u => [
          `"${u.full_name || ''}"`,
          `"${u.email || ''}"`,
          `"${u.phone || ''}"`,
          u.role,
          `"${u.location || ''}"`,
          u.created_at?.split('T')[0] || '',
          u.is_suspended ? 'Suspendu' : 'Actif'
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `utilisateurs_dockhta_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      toast.success(`${data.length} utilisateurs exportés`);
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield className="w-4 h-4 text-purple-500" />;
      case 'professional': return <Stethoscope className="w-4 h-4 text-red-500" />;
      case 'facility': return <Building2 className="w-4 h-4 text-indigo-500" />;
      case 'agent': return <UserCog className="w-4 h-4 text-amber-500" />;
      default: return <User className="w-4 h-4 text-blue-500" />;
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-700',
      professional: 'bg-red-100 text-red-600',
      facility: 'bg-indigo-100 text-indigo-700',
      agent: 'bg-amber-100 text-amber-700',
      patient: 'bg-blue-100 text-blue-700',
    };
    const labels = {
      admin: 'Admin',
      professional: 'Professionnel',
      facility: 'Établissement',
      agent: 'Agent',
      patient: 'Patient',
    };
    return (
      <Badge className={`text-xs ${colors[role] || colors.patient}`}>
        {labels[role] || role}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-4" data-testid="enhanced-users-list">
      {/* Filters Bar */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par nom, email, téléphone..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10 h-10"
                data-testid="user-search-input"
              />
            </div>
            
            {/* Role Filter */}
            <Select 
              value={filters.role} 
              onValueChange={(v) => {
                setFilters(f => ({ ...f, role: v }));
                setPagination(p => ({ ...p, page: 1 }));
              }}
            >
              <SelectTrigger className="w-full md:w-40 h-10" data-testid="role-filter">
                <SelectValue placeholder="Rôle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les rôles</SelectItem>
                <SelectItem value="patient">Patients</SelectItem>
                <SelectItem value="professional">Professionnels</SelectItem>
                <SelectItem value="facility">Établissements</SelectItem>
                <SelectItem value="agent">Agents</SelectItem>
                <SelectItem value="admin">Admins</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Status Filter */}
            <Select 
              value={filters.status} 
              onValueChange={(v) => {
                setFilters(f => ({ ...f, status: v }));
                setPagination(p => ({ ...p, page: 1 }));
              }}
            >
              <SelectTrigger className="w-full md:w-36 h-10" data-testid="status-filter">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="suspended">Suspendus</SelectItem>
                <SelectItem value="unverified">Non vérifiés</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Actions */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={loadUsers}
                className="h-10 w-10"
                title="Rafraîchir"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                onClick={handleExport}
                disabled={exporting}
                className="h-10"
                data-testid="export-users-btn"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              {pagination.total} utilisateur(s)
            </CardTitle>
            {loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-y">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Utilisateur</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase hidden md:table-cell">Contact</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Rôle</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase hidden sm:table-cell">Inscription</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Statut</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center">
                      <Loader2 className="w-6 h-6 text-slate-300 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-slate-400">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Aucun utilisateur trouvé</p>
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr 
                      key={user.id} 
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => {
                        setSelectedUser(user);
                        setShowUserModal(true);
                      }}
                      data-testid={`user-row-${user.id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                            {user.picture ? (
                              <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                            ) : (
                              getRoleIcon(user.role)
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 text-sm truncate">{user.full_name}</p>
                            <p className="text-xs text-slate-500 truncate md:hidden">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell">
                        <div className="text-sm">
                          <p className="text-slate-600 truncate max-w-[200px]">{user.email || '-'}</p>
                          <p className="text-xs text-slate-400">{user.phone || '-'}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {getRoleBadge(user.role)}
                      </td>
                      <td className="py-3 px-4 hidden sm:table-cell">
                        <span className="text-xs text-slate-500">{formatDate(user.created_at)}</span>
                      </td>
                      <td className="py-3 px-4">
                        {user.is_suspended ? (
                          <Badge className="bg-red-100 text-red-700 text-xs">Suspendu</Badge>
                        ) : user.is_verified ? (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">Actif</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">Non vérifié</Badge>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedUser(user);
                              setShowUserModal(true);
                            }}
                            className="h-8 w-8 p-0"
                            title="Voir détails"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {user.role !== 'admin' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleQuickSuspend(user.id, user.is_suspended);
                              }}
                              className={`h-8 w-8 p-0 ${user.is_suspended ? 'text-emerald-600' : 'text-amber-600'}`}
                              title={user.is_suspended ? 'Réactiver' : 'Suspendre'}
                            >
                              {user.is_suspended ? (
                                <CheckCircle className="w-4 h-4" />
                              ) : (
                                <Ban className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-xs text-slate-500">
                Page {pagination.page} sur {pagination.pages}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="h-8"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  className="h-8"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Detail Modal */}
      <UserDetailModal
        user={selectedUser}
        isOpen={showUserModal}
        onClose={() => {
          setShowUserModal(false);
          setSelectedUser(null);
        }}
        onUserUpdated={loadUsers}
      />
    </div>
  );
};

export default EnhancedUsersList;
